import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { InsuranceType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import { PersonTypes } from '@frontend/api-person';
import { ScheduleQueries } from '@frontend/api-schedule';
import { useTranslation } from '@frontend/i18n';
import {
  useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails,
  useHasWIVOrEIVAccess,
} from '@frontend/insurance-verification';
import { useAppScopeStore } from '@frontend/scope';
import { WivPromotionCard } from '@frontend/self-upgrade';
import { useContactPanelShallowStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Heading, SwitchChipGroup, useModalControl } from '@frontend/design-system';
import { History } from './components/history';
import { Insurance } from './components/insurance';
import { InsuranceBenefitSummary } from './components/insurance-benefit-summary';
import { InsuranceLoader } from './components/insurance-loader';
import NoInsuranceAccess from './components/no-insurance-access';
import { StyledCardContainer } from './components/styled-card-container';
import { useOpenInsuranceReport } from './hooks';
import useGetInsuranceDetails from './hooks/use-get-insurance-details';
import { useGetLocationVerticalForPayerList } from './hooks/use-get-location-vertical-for-payer-list';
import { useShouldShowInsurance } from './hooks/use-should-show-insurance';
import { useInsuranceType } from './providers/insurance-type-provider';
import { DateRangeOptionValue } from './utils/constants';
import { UndefinedOrNull } from './utils/types';
import {
  getNoInsuranceAccessMessage,
  getSwitchChipSelectedClassName,
  hasInNetworkOrOutNetworkData,
} from './utils/utils';

interface Props {
  locationId: string;
  personInfo: UndefinedOrNull<PersonTypes.Person>;
  showInNetworkOrOutNetworkData?: boolean;
  shouldShowHistoryData?: boolean;
}
export const InsuranceContainer = ({
  locationId,
  personInfo,
  showInNetworkOrOutNetworkData = true,
  shouldShowHistoryData = true,
}: Props) => {
  const ref = useRef<HTMLElement>(null);
  const { t } = useTranslation();

  const verificationMetricModalControl = useModalControl();
  const { accessibleLocationData, selectedLocationIds } = useAppScopeStore();
  const { locationIdWiseCustomizationFlagDetails } = useGetWeaveEnhancedInsuranceVerificationCustomizationFlagDetails();
  const isEnhancedInsuranceVerificationEnabled = locationIdWiseCustomizationFlagDetails?.[locationId!] || false;

  const [defaultDateRange, setDefaultDateRange] = useState<DateRangeOptionValue>('today');
  const { getHasInsuranceVerificationAccessForLocation } = useHasWIVOrEIVAccess();
  const { insuranceType, setInsuranceType } = useInsuranceType();
  const {
    isLoading: isWeaveTeaserLoading,
    showNoInsuranceAccessMessage,
    showWeaveVerifyPromotion,
  } = useShouldShowInsurance(locationId);
  const showInsurance = getHasInsuranceVerificationAccessForLocation(locationId);

  const {
    isLoading: isInsuranceDetailsLoading,
    insuranceDetails,
    hasActiveOrVerifiedInsurance,
    hasErrors,
    hasCoverage,
    insuranceVerificationAPIResData,
    isInsuranceDetailsWritebackSupported,
    isAutoVerification,
    requestId,
    personDetailsCoverage,
    isRefetching,
    errorDetails,
    refetchInsuranceDetails,
  } = useGetInsuranceDetails(locationId, personInfo?.PersonID || '', personInfo?.SourceID || '');

  const { data: locationData, isLoading: isListLocationsLoading } = ScheduleQueries.useListLocations(locationId);

  const isIntegratedOffice = locationData?.isIntegrated || false;

  const shouldShowTuuthFairyReport =
    insuranceVerificationAPIResData?.coverage?.thirdPartyCoverage?.partner === 'TuuthFairy';

  const { handleOpenInsuranceReport, isLoading: isLoadingInsuranceReport } = useOpenInsuranceReport({
    requestId,
    shouldShowTuuthFairyReport,
  });

  const { isDental } = useGetLocationVerticalForPayerList(locationId || '');

  const { scrollTo, setScrollTo } = useContactPanelShallowStore('scrollTo', 'setScrollTo');

  useEffect(() => {
    if (scrollTo === 'insurance-details') {
      ref.current?.scrollIntoView({ behavior: 'smooth' });
      setScrollTo(undefined);
    }
  }, [scrollTo, setScrollTo]);

  const isLoading =
    isInsuranceDetailsLoading || isWeaveTeaserLoading || isLoadingInsuranceReport || isListLocationsLoading;
  const hasHistoryData =
    insuranceDetails?.historyData && Object.keys(insuranceDetails.historyData).length > 0 && shouldShowHistoryData;
  const showInNetworkOrOutNetworkDetails =
    hasInNetworkOrOutNetworkData(insuranceDetails?.benefitLimits) && showInNetworkOrOutNetworkData;
  const shouldShowWriteBack =
    isInsuranceDetailsWritebackSupported &&
    personInfo?.SourceType === PersonTypes.PersonSourceType.Integration &&
    isDental &&
    hasActiveOrVerifiedInsurance;

  const updateDefaultDateRange = (range: DateRangeOptionValue) => {
    setDefaultDateRange(range);
    verificationMetricModalControl.openModal();
  };

  if (!showInsurance) return null;

  if (isLoading) {
    return <InsuranceLoader />;
  } else if (showNoInsuranceAccessMessage) {
    /**
          This message is shown only if it's a multi-location setup where child locations
          do have insurance access, but the current specific location does not.
       */
    const noAccessMessage = getNoInsuranceAccessMessage({
      accessibleLocationsData: accessibleLocationData,
      currentLocationId: locationId,
      selectedLocationIds,
      t,
    });

    return <NoInsuranceAccess message={noAccessMessage} t={t} />;
  } else if (showWeaveVerifyPromotion) {
    return (
      <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1), padding: theme.spacing(2) }}>
        <Heading level={3}>{t('Insurance')}</Heading>
        <WivPromotionCard locationIds={[locationId]} source='contacts' />
      </div>
    );
  }
  return (
    <>
      {isEnhancedInsuranceVerificationEnabled && (
        <SwitchChipGroup
          css={switchChip}
          singleSelect
          defaultValue={insuranceType}
          setValue={(val) => {
            setInsuranceType(val.toString() as InsuranceType);
          }}
          value={[insuranceType]}
        >
          <SwitchChipGroup.Option
            className={getSwitchChipSelectedClassName(insuranceType, InsuranceType.INSURANCE_TYPE_PRIMARY)}
            value={InsuranceType.INSURANCE_TYPE_PRIMARY}
          >
            {t('Primary')}
          </SwitchChipGroup.Option>
          <SwitchChipGroup.Option
            className={getSwitchChipSelectedClassName(insuranceType, InsuranceType.INSURANCE_TYPE_SECONDARY)}
            value={InsuranceType.INSURANCE_TYPE_SECONDARY}
          >
            {t('Secondary')}
          </SwitchChipGroup.Option>
        </SwitchChipGroup>
      )}
      <StyledCardContainer>
        <Insurance
          ref={ref}
          locationId={locationId}
          insuranceVerificationAPIResData={insuranceVerificationAPIResData}
          insuranceDetails={insuranceDetails}
          requestId={requestId}
          isEnhancedInsuranceVerificationEnabled={isEnhancedInsuranceVerificationEnabled}
          hasErrors={hasErrors}
          hasCoverage={hasCoverage}
          hasActiveOrVerifiedInsurance={hasActiveOrVerifiedInsurance}
          isAutoVerification={isAutoVerification}
          shouldShowWriteBack={shouldShowWriteBack}
          personDetailsCoverage={personDetailsCoverage}
          defaultDateRange={defaultDateRange}
          isReFetching={isRefetching || false}
          refetchInsuranceDetails={refetchInsuranceDetails}
          updateDefaultDateRange={updateDefaultDateRange}
          handleOpenInsuranceReport={handleOpenInsuranceReport}
          errorDetails={errorDetails}
          verificationMetricModalControl={verificationMetricModalControl}
          isIntegratedOffice={isIntegratedOffice}
        />
      </StyledCardContainer>
      {showInNetworkOrOutNetworkDetails && (
        <StyledCardContainer>
          <InsuranceBenefitSummary
            benefitDetails={insuranceDetails?.benefitLimits}
            handleOpenInsuranceReport={handleOpenInsuranceReport}
          />
        </StyledCardContainer>
      )}

      {hasHistoryData && (
        <StyledCardContainer>
          <History stepsData={insuranceDetails?.historyData} />
        </StyledCardContainer>
      )}
    </>
  );
};
InsuranceContainer.displayName = 'InsuranceContainer';

const switchChip = css`
  gap: ${theme.spacing(2)};
  margin-bottom: ${theme.spacing(2)};
  span {
    color: ${theme.colors.neutral90};
  }
  .selected,
  .selected:hover {
    background: ${theme.colors.primary5};
    border: solid 1px ${theme.colors.primary50};
  }
  .chip {
    :focus {
      outline: none;
    }
  }
`;
